import { useMemo, useState } from 'react';

import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import type { Setting } from '@/types';
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';

type ProjectTaskTemplateTask = {
    department_key: string;
    name: string;
};

type ProjectTaskTemplate = {
    value: string;
    label: string;
    tasks: ProjectTaskTemplateTask[];
};

interface ProjectTaskTemplatesEditorProps {
    settingKey: string;
    setting: Setting;
    projectTaskDepartments?: Array<{ value: string; label?: string; roles?: string[] }>;
    availableRoles?: string[];
    onUpdate: (key: string, value: ProjectTaskTemplate[]) => void;
}

const normalizeTemplatesValue = (value: unknown): ProjectTaskTemplate[] => {
    const rawItems: unknown[] = Array.isArray(value)
        ? value
        : value && typeof value === 'object'
          ? Object.entries(value as Record<string, unknown>).map(([key, templateValue]) => {
                if (templateValue && typeof templateValue === 'object') {
                    return { value: key, ...(templateValue as Record<string, unknown>) };
                }
                return { value: key, tasks: Array.isArray(templateValue) ? templateValue : [] };
            })
          : [];

    return rawItems
        .map((item) => {
            if (!item || typeof item !== 'object') {
                return null;
            }

            const record = item as Record<string, unknown>;
            const tasksRaw = Array.isArray(record.tasks) ? record.tasks : [];

            const tasks = tasksRaw
                .map((task) => {
                    if (!task || typeof task !== 'object') {
                        return null;
                    }
                    const taskRecord = task as Record<string, unknown>;
                    const department_key =
                        typeof taskRecord.department_key === 'string'
                            ? taskRecord.department_key
                            : typeof taskRecord.type === 'string'
                              ? taskRecord.type
                              : '';
                    const name = typeof taskRecord.name === 'string' ? taskRecord.name : '';
                    return { department_key, name };
                })
                .filter((task): task is ProjectTaskTemplateTask => Boolean(task && task.name));

            const templateValue =
                typeof record.value === 'string'
                    ? record.value
                    : typeof record.key === 'string'
                      ? record.key
                      : typeof record.name === 'string'
                        ? record.name
                        : '';
            const templateLabel = typeof record.label === 'string' ? record.label : typeof record.name === 'string' ? record.name : templateValue;

            if (!templateValue) {
                return null;
            }

            return {
                value: templateValue,
                label: templateLabel,
                tasks,
            } satisfies ProjectTaskTemplate;
        })
        .filter((template): template is ProjectTaskTemplate => Boolean(template));
};

export function ProjectTaskTemplatesEditor({
    settingKey,
    setting,
    projectTaskDepartments = [],
    availableRoles = [],
    onUpdate,
}: ProjectTaskTemplatesEditorProps) {
    const initialTemplates = useMemo(() => normalizeTemplatesValue(setting.value), [setting.value]);
    const [templates, setTemplates] = useState<ProjectTaskTemplate[]>(initialTemplates);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editingTemplate, setEditingTemplate] = useState<ProjectTaskTemplate | null>(null);

    const departmentOptions = useMemo(() => {
        const built = (projectTaskDepartments ?? [])
            .filter((item) => item && typeof item.value === 'string' && item.value.trim().length > 0)
            .map((item) => ({
                value: String(item.value),
                label: String(item.label || item.value),
            }));

        availableRoles.forEach((roleName) => {
            built.push({
                value: `role:${roleName}`,
                label: roleName,
            });
        });

        return built;
    }, [projectTaskDepartments, availableRoles]);

    const openEdit = (index: number) => {
        setEditingIndex(index);
        setEditingTemplate({ ...templates[index], tasks: templates[index].tasks.map((t) => ({ ...t })) });
    };

    const addTemplate = () => {
        const newTemplate: ProjectTaskTemplate = {
            value: `template_${templates.length + 1}`,
            label: `Template ${templates.length + 1}`,
            tasks: [],
        };
        const next = [...templates, newTemplate];
        setTemplates(next);
        setEditingIndex(next.length - 1);
        setEditingTemplate({ ...newTemplate });
    };

    const removeTemplate = (index: number) => {
        setTemplates(templates.filter((_, i) => i !== index));
    };

    const saveEdit = () => {
        if (editingIndex === null || !editingTemplate) return;
        const next = templates.map((t, i) => (i === editingIndex ? editingTemplate : t));
        setTemplates(next);
        onUpdate(settingKey, next);
        setEditingIndex(null);
        setEditingTemplate(null);
    };

    const closeEdit = () => {
        setEditingIndex(null);
        setEditingTemplate(null);
    };

    const updateEditingTemplate = (patch: Partial<ProjectTaskTemplate>) => {
        if (!editingTemplate) return;
        setEditingTemplate({ ...editingTemplate, ...patch });
    };

    const addTask = () => {
        if (!editingTemplate) return;
        const defaultDepartment = departmentOptions[0]?.value ?? '';
        setEditingTemplate({
            ...editingTemplate,
            tasks: [...editingTemplate.tasks, { department_key: defaultDepartment, name: '' }],
        });
    };

    const updateTask = (taskIndex: number, patch: Partial<ProjectTaskTemplateTask>) => {
        if (!editingTemplate) return;
        const tasks = editingTemplate.tasks.map((task, j) => (j === taskIndex ? { ...task, ...patch } : task));
        setEditingTemplate({ ...editingTemplate, tasks });
    };

    const removeTask = (taskIndex: number) => {
        if (!editingTemplate) return;
        setEditingTemplate({ ...editingTemplate, tasks: editingTemplate.tasks.filter((_, j) => j !== taskIndex) });
    };

    const moveTask = (taskIndex: number, direction: 'up' | 'down') => {
        if (!editingTemplate) return;
        const tasks = [...editingTemplate.tasks];
        const targetIndex = direction === 'up' ? taskIndex - 1 : taskIndex + 1;
        if (targetIndex < 0 || targetIndex >= tasks.length) return;
        const [item] = tasks.splice(taskIndex, 1);
        tasks.splice(targetIndex, 0, item);
        setEditingTemplate({ ...editingTemplate, tasks });
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
                <div className="space-y-1">
                    <div className="text-sm font-medium">Project task templates</div>
                    <div className="text-muted-foreground text-xs">Manage templates used to create default Gantt tasks for projects.</div>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addTemplate}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add template
                </Button>
            </div>

            {templates.length === 0 ? (
                <div className="text-muted-foreground rounded-md border border-dashed p-4 text-sm">No templates yet.</div>
            ) : (
                <div className="rounded-md border">
                    {templates.map((template, index) => (
                        <div key={`template-${index}`} className="flex items-center justify-between border-b px-4 py-2.5 last:border-b-0">
                            <button
                                type="button"
                                onClick={() => openEdit(index)}
                                className="text-primary text-left text-sm font-medium hover:underline"
                            >
                                {template.label || template.value}
                            </button>
                            <div className="flex items-center gap-2">
                                <span className="text-muted-foreground text-xs">{template.tasks.length} tasks</span>
                                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeTemplate(index)}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ResponsiveModal
                open={editingIndex !== null}
                onOpenChange={(open) => {
                    if (!open) closeEdit();
                }}
                variant="sheet"
            >
                <ResponsiveModalContent size="xl">
                    <ResponsiveModalHeader>
                        <ResponsiveModalTitle>{editingTemplate?.label || 'Edit Template'}</ResponsiveModalTitle>
                    </ResponsiveModalHeader>

                    <ResponsiveModalBody>
                        {editingTemplate && (
                            <div className="space-y-4">
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label>Value</Label>
                                        <Input
                                            value={editingTemplate.value}
                                            onChange={(e) => updateEditingTemplate({ value: e.target.value })}
                                            placeholder="e.g., standard"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Label</Label>
                                        <Input
                                            value={editingTemplate.label}
                                            onChange={(e) => updateEditingTemplate({ label: e.target.value })}
                                            placeholder="e.g., Standard"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center justify-between gap-2">
                                    <div className="text-sm font-medium">Tasks</div>
                                    <Button type="button" variant="outline" size="sm" onClick={addTask}>
                                        <Plus className="mr-2 h-4 w-4" />
                                        Add task
                                    </Button>
                                </div>

                                {editingTemplate.tasks.length === 0 ? (
                                    <div className="text-muted-foreground rounded-md border border-dashed p-4 text-sm">
                                        No tasks in this template.
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {editingTemplate.tasks.map((task, taskIndex) => (
                                            <div key={`task-${taskIndex}`} className="grid gap-3 rounded-md border p-3 md:grid-cols-[220px_1fr_auto]">
                                                <div className="space-y-2">
                                                    <Label>Department</Label>
                                                    <Select
                                                        value={task.department_key}
                                                        onValueChange={(value) => updateTask(taskIndex, { department_key: value })}
                                                    >
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Select department" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {departmentOptions.map((opt) => (
                                                                <SelectItem key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>

                                                <div className="space-y-2">
                                                    <Label>Name</Label>
                                                    <Input
                                                        value={task.name}
                                                        onChange={(e) => updateTask(taskIndex, { name: e.target.value })}
                                                        placeholder="Task name"
                                                    />
                                                </div>

                                                <div className="flex items-end gap-1">
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => moveTask(taskIndex, 'up')}
                                                        disabled={taskIndex === 0}
                                                    >
                                                        <ChevronUp className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => moveTask(taskIndex, 'down')}
                                                        disabled={taskIndex === editingTemplate.tasks.length - 1}
                                                    >
                                                        <ChevronDown className="h-4 w-4" />
                                                    </Button>
                                                    <Button type="button" variant="ghost" size="icon" onClick={() => removeTask(taskIndex)}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </ResponsiveModalBody>

                    <ResponsiveModalFooter>
                        <Button type="button" variant="outline" onClick={closeEdit}>
                            Cancel
                        </Button>
                        <Button type="button" onClick={saveEdit}>
                            Save
                        </Button>
                    </ResponsiveModalFooter>
                </ResponsiveModalContent>
            </ResponsiveModal>
        </div>
    );
}
